import React from 'react'
import { Sidebar, Button, Text, Input, LoadingSpinner, Heading } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { crmService } from '../../services/crm'
import { CRMActivity, CRMActivityType, CRMRelatedType } from '../../types/crm'

const CRMAtividades: React.FC = () => {
  const [loading, setLoading] = React.useState<boolean>(true)
  const [items, setItems] = React.useState<CRMActivity[]>([])
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<Partial<CRMActivity> | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    const data = await crmService.listActivities()
    setItems(data)
    setLoading(false)
  }, [])

  React.useEffect(() => { load() }, [load])

  const openNew = () => {
    setEditing({
      type: 'task' as CRMActivityType,
      title: '',
      notes: '',
      due_at: '',
      related_type: 'company' as CRMRelatedType,
      related_id: '',
    })
    setIsModalOpen(true)
  }

  const openEdit = (a: CRMActivity) => {
    setEditing({ ...a })
    setIsModalOpen(true)
  }

  const save = async () => {
    if (!editing?.title || !editing?.type || !editing?.related_type || !editing?.related_id) return
    await crmService.upsertActivity(editing as any)
    setIsModalOpen(false)
    setEditing(null)
    await load()
  }

  const markDone = async (a: CRMActivity) => {
    await crmService.upsertActivity({ id: a.id, completed_at: new Date().toISOString() } as any)
    await load()
  }

  const remove = async (id: string) => {
    if (!window.confirm('Excluir esta atividade?')) return
    await crmService.deleteActivity(id)
    await load()
  }

  return (
    <div className="flex h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
        <PermissionGuard moduleName="crm_atividades" permissionType="show">
          <div className="py-6 px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">Atividades</Heading>
                <Text size="sm" color="muted" className="mt-2">Agenda e tarefas do CRM (sem automações)</Text>
              </div>
              <PermissionGuard moduleName="crm_atividades" permissionType="insert" fallback={null}>
                <Button variant="solid" onClick={openNew}>Nova atividade</Button>
              </PermissionGuard>
            </div>
            {/* Lista simples */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/30">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Título</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vencimento</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center"><LoadingSpinner /></td>
                    </tr>
                  ) : items.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        Nenhuma atividade.
                      </td>
                    </tr>
                  ) : (
                    items.map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{a.type}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">{a.title}</td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                          {a.due_at ? new Date(a.due_at).toLocaleString('pt-BR') : '—'}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                          {a.completed_at ? 'Concluída' : 'Pendente'}
                        </td>
                        <td className="px-4 py-2 text-right space-x-2">
                          {!a.completed_at && (
                            <PermissionGuard moduleName="crm_atividades" permissionType="update" fallback={null}>
                              <Button variant="outline" size="sm" onClick={() => markDone(a)}>Concluir</Button>
                            </PermissionGuard>
                          )}
                          <PermissionGuard moduleName="crm_atividades" permissionType="update" fallback={null}>
                            <Button variant="outline" size="sm" onClick={() => openEdit(a)}>Editar</Button>
                          </PermissionGuard>
                          <PermissionGuard moduleName="crm_atividades" permissionType="delete" fallback={null}>
                            <Button variant="ghost" size="sm" onClick={() => remove(a.id)}>Excluir</Button>
                          </PermissionGuard>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Modal */}
            {isModalOpen && editing && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Text size="lg" className="text-gray-900 dark:text-white">
                      {editing?.id ? 'Editar atividade' : 'Nova atividade'}
                    </Text>
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Fechar</Button>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Tipo</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={editing.type as string}
                          onChange={(e) => setEditing(prev => ({ ...prev!, type: e.target.value as CRMActivityType }))}
                        >
                          <option value="task">Tarefa</option>
                          <option value="call">Ligação</option>
                          <option value="meeting">Reunião</option>
                          <option value="followup">Follow-up</option>
                        </select>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Vencimento</Text>
                        <Input
                          type="datetime-local"
                          value={editing.due_at || ''}
                          onChange={(e: any) => setEditing(prev => ({ ...prev!, due_at: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Título</Text>
                      <Input
                        value={editing.title || ''}
                        onChange={(e: any) => setEditing(prev => ({ ...prev!, title: e.target.value }))}
                        placeholder="Descreva a atividade"
                      />
                    </div>

                    <div>
                      <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Notas</Text>
                      <Input
                        value={editing.notes || ''}
                        onChange={(e: any) => setEditing(prev => ({ ...prev!, notes: e.target.value }))}
                        placeholder="Opcional"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">Vincular a</Text>
                        <select
                          className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          value={editing.related_type as string}
                          onChange={(e) => setEditing(prev => ({ ...prev!, related_type: e.target.value as CRMRelatedType }))}
                        >
                          <option value="company">Empresa</option>
                          <option value="contact">Contato</option>
                          <option value="opportunity">Oportunidade</option>
                        </select>
                      </div>
                      <div>
                        <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-1">ID do registro vinculado</Text>
                        <Input
                          value={(editing.related_id as string) || ''}
                          onChange={(e: any) => setEditing(prev => ({ ...prev!, related_id: e.target.value }))}
                          placeholder="Cole o ID do registro"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => { setIsModalOpen(false); setEditing(null) }}>Cancelar</Button>
                    <Button variant="solid" onClick={save}>Salvar</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </PermissionGuard>
      </div>
    </div>
  )
}

export default CRMAtividades
